import moment from 'moment';
import { AppReview } from '@capawesome/capacitor-app-review';
import { Preferences } from '@capacitor/preferences';
import * as api from '../user/api';
import { UPDATE_SUBSCRIPTIONS } from '../userSubscription/duck';
import { LOCALE_SELECTED } from '../locale/action';
import { ERROR, INIT, LOADING, SUCCESS } from '../../constants/phase';
import { GET_USER_SUCCESS, loginFetch } from '../user/duck';
import NativeOrWeb from '../../utils/native';
import { AuthRequestError, isAuthSessionCancelled } from '../../utils/auth-session-errors';
import { isLogoutPending, isLogoutConfirmed, startLogoutRetry, markLogoutConfirmed } from '../../utils/auth-refresh';

/***********************************
 * Action Types
 ***********/
export const AUTHENTICATE_USER = 'AUTHENTICATE_USER';
export const AUTHENTICATE_USER_SUCCESS = 'AUTHENTICATE_USER_SUCCESS';
export const AUTHENTICATE_USER_ERROR = 'AUTHENTICATE_USER_ERROR';
export const AUTHENTICATE_USER_TRANSIENT = 'AUTHENTICATE_USER_TRANSIENT';
export const AUTH_BOOTSTRAP_ERROR = 'AUTH_BOOTSTRAP_ERROR';
export const UPDATE_IS_BIOMETRIC_AVAILABLE = 'UPDATE_IS_BIOMETRIC_AVAILABLE';
export const UPDATE_IS_BIOMETRIC_SET = 'UPDATE_IS_BIOMETRIC_SET';
export const RESET_AUTH = 'RESET_AUTH';

/***********************************
 * Initial State
 ***********/
const InitialState = {
  initialAuthDone: false,
  isAuthenticated: false,
  authenticatedUser: null,
  authenticationPhase: INIT,
  authenticationError: null,
  authRetryCount: 0,
  bootstrapError: null,

  isBiometricAvailable: false,
  isBiometricSet: false
};

/***********************************
 * Reducer
 ***********/
export default function(state = InitialState, action = {}) {
  switch (action.type) {
    case AUTHENTICATE_USER: {
      return {
        ...state,
        authenticationPhase: LOADING,
        authenticationError: null
      };
    }

    case AUTHENTICATE_USER_SUCCESS: {
      NativeOrWeb.saveAccessTokenCookie()
        .then()
        .catch();
      const validUser = action.payload && action.payload._id ? action.payload : null;
      return {
        ...state,
        initialAuthDone: true,
        isAuthenticated: !!validUser,
        authenticatedUser: validUser,
        authenticationPhase: SUCCESS,
        authenticationError: null,
        authRetryCount: 0,
        bootstrapError: null
      };
    }

    case AUTHENTICATE_USER_TRANSIENT: {
      return {
        ...state,
        authenticationPhase: ERROR,
        authenticationError: action.payload,
        authRetryCount: (state.authRetryCount || 0) + 1
      };
    }

    case AUTH_BOOTSTRAP_ERROR: {
      return {
        ...state,
        bootstrapError: action.payload
      };
    }

    case AUTHENTICATE_USER_ERROR: {
      return {
        ...state,
        initialAuthDone: true,
        isAuthenticated: false,
        authenticatedUser: null,
        authenticationPhase: ERROR,
        authenticationError: action.payload
      };
    }

    case UPDATE_IS_BIOMETRIC_AVAILABLE: {
      return {
        ...state,
        isBiometricAvailable: action.payload
      };
    }

    case UPDATE_IS_BIOMETRIC_SET: {
      return {
        ...state,
        isBiometricSet: action.payload
      };
    }

    case RESET_AUTH: {
      localStorage.removeItem('clientId');
      return {
        ...state,
        initialAuthDone: false,
        isAuthenticated: false,
        authenticatedUser: null,
        authenticationPhase: INIT,
        authenticationError: null,
        authRetryCount: 0
      };
    }

    default: {
      return state;
    }
  }
}

/***********************************
 * Action Creators
 ***********/
export const authenticateUser = () => async (dispatch, getState) => {
  try {
    if (isLogoutPending()) {
      if (!isLogoutConfirmed()) {
        startLogoutRetry(async () => {
          try {
            const payload = await api.logoutUser();
            if (!payload || payload.success !== true) {
              return false;
            }
            markLogoutConfirmed();
            return true;
          } catch (_) {
            return false;
          }
        });
      }
      dispatch({ type: AUTHENTICATE_USER_ERROR, payload: 'logout_pending' });
      dispatch({ type: UPDATE_SUBSCRIPTIONS, payload: [] });
      return;
    }

    dispatch({ type: AUTHENTICATE_USER });

    // check biometric authentication
    await checkIsBiometricSet()(dispatch, getState);

    // for native
    const accessToken = await NativeOrWeb.getAccessTokenCookie();

    const payload = await api.authenticateUser(accessToken);

    if (!payload || !payload._id) {
      throw new AuthRequestError({ kind: 'session_dead', message: 'auth_invalid_payload' });
    }

    dispatch({
      type: UPDATE_SUBSCRIPTIONS,
      payload: payload.subscriptions ? payload.subscriptions : []
    });

    if (payload.language) {
      dispatch({ type: LOCALE_SELECTED, locale: payload.language });
    }

    if (payload.userType) {
      const isUser = payload.userType === 'User';

      if (isUser) {
        localStorage.removeItem('clientId');
      }

      const showAdviserBlast = isUser;
      try {
        const result = await Promise.all([
          loginFetch(payload._id, payload.userType, { state: { showAdviserBlast: showAdviserBlast } })(
            dispatch,
            getState
          ),
          api.getUser()
        ]);
        dispatch({ type: GET_USER_SUCCESS, payload: result[1] });
      } catch (bootstrapError) {
        // Codex #1: same cancel rule as the outer catch. Epoch-only is not
        // enough — onUserSignOut() does not bump epoch.
        if (isAuthSessionCancelled(bootstrapError)) {
          throw bootstrapError;
        }
        if (bootstrapError && bootstrapError.kind === 'session_dead') {
          dispatch({ type: AUTHENTICATE_USER_ERROR, payload: 'session_dead' });
          dispatch({ type: UPDATE_SUBSCRIPTIONS, payload: [] });
          return;
        }
        dispatch({
          type: AUTHENTICATE_USER_SUCCESS,
          payload
        });
        dispatch({
          type: AUTH_BOOTSTRAP_ERROR,
          payload: (bootstrapError && bootstrapError.kind) || 'bootstrap_failed'
        });
        return;
      }
    }

    dispatch({
      type: AUTHENTICATE_USER_SUCCESS,
      payload: payload
    });

    if (NativeOrWeb.isNativePlatform()) {
      let { value: firstLoginDate } = await Preferences.get({ key: 'firstLoginDate' });
      if (firstLoginDate) {
        let { value: reviewRequestedDate } = await Preferences.get({ key: 'reviewRequestedDate' });
        if (
          moment(firstLoginDate)
            .add(2, 'weeks')
            .isBefore(moment()) &&
          (!reviewRequestedDate ||
            moment(reviewRequestedDate)
              .add(2, 'months')
              .isBefore(moment()))
        ) {
          try {
            await AppReview.requestReview();
            await Preferences.set({ key: 'reviewRequestedDate', value: moment().format() });
          } catch (e) {
            console.log(e);
          }
        }
      } else {
        await Preferences.set({ key: 'firstLoginDate', value: moment().format() });
      }
    }
  } catch (error) {
    if (isAuthSessionCancelled(error)) {
      return;
    }
    const midSession = getState().auth.isAuthenticated;
    const kind = error && error.kind;
    if (kind === 'session_dead') {
      dispatch({ type: AUTHENTICATE_USER_ERROR, payload: error.toString() });
      dispatch({ type: UPDATE_SUBSCRIPTIONS, payload: [] });
      return;
    }
    dispatch({
      type: AUTHENTICATE_USER_TRANSIENT,
      payload: error && error.toString ? error.toString() : 'transient'
    });
    if (!midSession) {
      // Cold start stays unresolved: initialAuthDone remains false.
      return;
    }
  }
};

export const checkIsBiometricSet = () => async (dispatch, getState) => {
  const isBiometricAvailable = await NativeOrWeb.isBiometricAvailable();
  dispatch({ type: UPDATE_IS_BIOMETRIC_AVAILABLE, payload: isBiometricAvailable });

  const isBiometricSet = await NativeOrWeb.isBiometricSet();
  dispatch({ type: UPDATE_IS_BIOMETRIC_SET, payload: isBiometricSet });
};

export const updateIsBiometricSet = isBiometricSet => ({
  type: UPDATE_IS_BIOMETRIC_SET,
  payload: isBiometricSet
});

export const resetAuth = () => ({
  type: RESET_AUTH
});
